import React from 'react';
import { Box, Typography, Stack, Button, Fade } from '@mui/material';
import RestoreIcon from '@mui/icons-material/Restore';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useTranslation } from 'react-i18next';
import { glassCard, ACCENT, TEXT_PRIMARY, TEXT_SECONDARY, noMotion } from '../../designsystem/tokens';

const DRAFT_KEY = 'labia-draft';
const DRAFT_MAX_AGE_MS = 30 * 60 * 1000; // 30 minutes

export function getDraft() {
    try {
        const raw = localStorage.getItem(DRAFT_KEY);
        if (!raw) return null;
        const draft = JSON.parse(raw);
        if (!draft.timestamp || Date.now() - draft.timestamp > DRAFT_MAX_AGE_MS) {
            localStorage.removeItem(DRAFT_KEY);
            return null;
        }
        return draft;
    } catch {
        localStorage.removeItem(DRAFT_KEY);
        return null;
    }
}

export function saveDraft(json, text, options) {
    try {
        localStorage.setItem(DRAFT_KEY, JSON.stringify({
            json,
            text,
            options,
            timestamp: Date.now(),
        }));
    } catch {
        // localStorage full or unavailable — silently fail
    }
}

export function clearDraft() {
    localStorage.removeItem(DRAFT_KEY);
}

function getTimeAgo(timestamp) {
    const diff = Math.floor((Date.now() - timestamp) / 1000);
    if (diff < 60) return `< 1 min`;
    if (diff < 3600) return `${Math.floor(diff / 60)} min`;
    return `${Math.floor(diff / 3600)}h`;
}

const DraftBanner = ({ draft, onResume, onDiscard }) => {
    const { t } = useTranslation();
    if (!draft) return null;

    const age = getTimeAgo(draft.timestamp, t);

    return (
        <Fade in={true}>
            <Box
                data-testid="draft-banner"
                role="alert"
                sx={{
                    ...glassCard,
                    position: 'fixed',
                    bottom: 'calc(104px + 16px)',
                    left: '5%',
                    right: '5%',
                    width: '90%',
                    zIndex: 2,
                    border: `1px solid ${ACCENT}30`,
                    p: 2,
                }}
            >
                <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1.5 }}>
                    <RestoreIcon sx={{ color: ACCENT, fontSize: 22 }} />
                    <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" sx={{ color: TEXT_PRIMARY, fontWeight: 600, fontSize: '0.88rem' }}>
                            {t('draftFound')}
                        </Typography>
                        <Typography variant="caption" sx={{ color: TEXT_SECONDARY, fontSize: '0.75rem' }}>
                            {age}
                        </Typography>
                    </Box>
                </Stack>
                <Stack direction="row" spacing={1}>
                    <Button
                        fullWidth
                        variant="contained"
                        data-testid="draft-resume"
                        startIcon={<RestoreIcon />}
                        onClick={onResume}
                        sx={{
                            minHeight: 44,
                            borderRadius: '10px',
                            textTransform: 'none',
                            fontWeight: 600,
                            backgroundColor: ACCENT,
                            '&:hover': { backgroundColor: '#5e9294' },
                        }}
                    >
                        {t('resumeDraft')}
                    </Button>
                    <Button
                        fullWidth
                        variant="outlined"
                        data-testid="draft-discard"
                        startIcon={<DeleteOutlineIcon />}
                        onClick={onDiscard}
                        sx={{
                            minHeight: 44,
                            borderRadius: '10px',
                            textTransform: 'none',
                            color: TEXT_PRIMARY,
                            borderColor: 'rgba(255,255,255,0.2)',
                            '&:hover': { borderColor: 'rgba(255,255,255,0.4)' },
                        }}
                    >
                        {t('discardDraft')}
                    </Button>
                </Stack>
            </Box>
        </Fade>
    );
};

export default DraftBanner;
